/**
 * SOLID Review
 * S — Strong. One concern: the object behind the frontend Domain's lollipop — a method per decision,
 *     each forwarding to the pure function that decides.
 * O — Good. A new decision is a new method here and a new row in the contract.
 * L — Strong. Implements `IPortClientDecisions` exactly; every method is async like the port.
 * I — Strong. Exactly the port's surface.
 * D — Strong. The decision functions and the port's types. No port of its own, no I/O.
 */
import type { IContextRequest } from '../ports/schemas/ContextRequest';
import type { IPortClientDecisions } from '../ports/ClientDecisions.contract';
import type { IPropsClientTableInput } from '../ports/schemas/ClientTable';
import type {
  IPayloadRoundStarted, IPayloadClaimAnnounced, IPayloadCupLifted, IPayloadDiceRolled, IPayloadRoomRoster, IPayloadPresence,
  IPayloadRoomAbandoned, IPayloadDecisionCountdown, IPayloadTableView, IPayloadConnectivity,
} from '../ports/schemas/TableEvents';
import type {
  ICommandNothing, ICommandAnnounce, ICommandLeave, ICommandGameStarted, ICommandRoundMoveAnswered, ICommandPeeked,
  ICommandClaimAccepted, ICommandBelieveAccepted, ICommandPlayAgainAnswered, ICommandActiveRoundRestored,
  ICommandSuspendedReconciled, ICommandLobbyActiveRound, ICommandLobbyRoomLoaded,
} from '../ports/schemas/TableMoves';
import {
  decideRoundStarted, decideClaimAnnounced, decideCupLifted, decideDiceRolled, decideRoomUpdated, decidePlayerSuspended,
  decidePlayerResumed, decideRoomAbandoned, decideDecisionCountdown, decideConnectivity,
} from './table/serverEvents';
import {
  decideTableView, decidePlanResync, decideActiveRoundRestored, decideSuspendedReconciled, decidePlanLobbyRead,
  decideLobbyActiveRound, decideLobbyRoomLoaded,
} from './table/catchUp';
import {
  decidePlanStartGame, decideGameStarted, decidePlanRoll, decideRolled, decidePlanPeek, decidePeeked, decidePlanAnnounce,
  decideClaimAccepted, decidePlanBelieve, decideBelieveAccepted, decidePlanRollFirst, decideRollFirstAccepted, decidePlanLift,
  decideLiftAnswered, decidePlanPlayAgain, decidePlayAgainAnswered, decideLeaveTable,
} from './table/localMoves';
import type { IPropsClientInput } from '../ports/schemas/ClientChanges';
import type {
  ICommandActiveRoomFound, ICommandCreateGuest, ICommandGuestCreated, ICommandJoinRouted, ICommandMeAnswered, ICommandNavigate, ICommandPlanEntry, ICommandPlanIntent, ICommandPlanRestore, ICommandRestoreJoined, ICommandResumeValidated, ICommandRoundRestored, ICommandSessionStarted, ICommandTableCode, ICommandTableCodeChecked, ICommandTableHosted, ICommandTableJoined, ICommandTableLateJoined, ICommandTableRejoined,
} from '../ports/schemas/SessionMoves';
import {
  decideSessionStarted, decidePlanEntry, decideMeAnswered, decideMeUnreachable, decideResumeValidated, decidePlanRestore, decideRestoreJoined, decideRoundRestored, decidePlanIntent, decideEntryFinished,
} from './session/entry';
import {
  decidePlanJoin, decideJoinRouted, decideTableJoined, decideTableRejoined, decideTableLateJoined, decidePlanHost, decideTableHosted, decidePlanColdRejoin, decideActiveRoomFound,
} from './session/seating';
import {
  decidePlanGuest, decideGuestCreated, decidePlanTableCode, decideTableCodeChecked, decideNavigate, decideDismissJoinError,
} from './session/funnel';
import {
  decidePlanLoginCode, decideLoginCodeAnswered, decidePlanSignUp, decideSignedUp, decidePlanVerifyCode, decideSignedIn,
  decideProfileLoaded, decidePlanNickname, decideNicknameChanged, decidePlanEmailChange, decidePlanVerifyEmailChange,
  decideEmailChanged, decideSignedOut,
} from './session/auth';
import { decidePlanLegalReview, decideLegalReviewOf, decidePlanLegalAcceptance } from './legal/legalReview';
import { decideConsentChanged, decidePlanCookieConsentRecord } from './legal/cookieConsent';
import type { ICommandConsentChanged, ICommandCookieConsentRecord } from '../ports/schemas/ConsentMoves';
import type { ICommandCreditsChanged, ICommandBalanceRead } from '../ports/schemas/PaymentMoves';
import { decideCreditsChanged, decideBalanceRead, decidePlanAccountWatch } from './payment/credits';
import type { ICommandAdConfigLoaded } from '../ports/schemas/AdsMoves';
import { decidePlanAdConfig, decideAdConfigLoaded } from './ads/adSlot';
import type { ICommandMediaClock, ICommandMediaIdle, ICommandMediaBanner, ICommandMediaUsed } from '../ports/schemas/MediaMoves';
import {
  decideMediaToggled, decideMediaReconnect, decideMediaIdleChecked, decideMediaRemovedByServer, decideMediaBannerDismissed, decidePlanPresence,
} from './media/mediaPolicy';
import type { ICommandPlanLegalReview, ICommandLegalReviewOf, ICommandLegalAcceptance } from '../ports/schemas/LegalMoves';
import type {
  ICommandEmail, ICommandLoginCodeAnswered, ICommandSignUp, ICommandSignedUp, ICommandVerifyCode, ICommandSignedIn,
  ICommandProfileLoaded, ICommandNickname, ICommandEmailCode, ICommandEmailChanged,
} from '../ports/schemas/AuthMoves';

/**
 * ASYNC IN SIGNATURE, PURE IN FACT (as the backend's `DomainDecisions`). Every method returns a Promise
 * because the port is declared async — uniform with every other non-local port, so the lollipop can be
 * remoted — but none of them awaits anything: a decision is a function of its arguments. `ctx` is
 * accepted and unread; the Domain does not know what a request is. Unlike the backend's, there is no
 * environment to bind: a client decision reads no clock and mints no id.
 */
export class ClientDecisions implements IPortClientDecisions {
  async roundStarted(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadRoundStarted) {
    return decideRoundStarted(input, event);
  }

  async claimAnnounced(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadClaimAnnounced) {
    return decideClaimAnnounced(input, event);
  }

  async cupLifted(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadCupLifted) {
    return decideCupLifted(input, event);
  }

  async diceRolled(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadDiceRolled) {
    return decideDiceRolled(input, event);
  }

  async roomUpdated(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadRoomRoster) {
    return decideRoomUpdated(input, event);
  }

  async playerSuspended(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadPresence) {
    return decidePlayerSuspended(input, event);
  }

  async playerResumed(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadPresence) {
    return decidePlayerResumed(input, event);
  }

  async roomAbandoned(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadRoomAbandoned) {
    return decideRoomAbandoned(input, event);
  }

  async decisionCountdown(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadDecisionCountdown) {
    return decideDecisionCountdown(input, event);
  }

  async tableView(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadTableView) {
    return decideTableView(input, event);
  }

  async connectivity(_ctx: IContextRequest, input: IPropsClientTableInput, event: IPayloadConnectivity) {
    return decideConnectivity(input, event);
  }

  async planResync(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanResync(input, command);
  }

  async activeRoundRestored(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandActiveRoundRestored) {
    return decideActiveRoundRestored(input, command);
  }

  async suspendedReconciled(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandSuspendedReconciled) {
    return decideSuspendedReconciled(input, command);
  }

  async planLobbyRead(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanLobbyRead(input, command);
  }

  async lobbyActiveRound(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandLobbyActiveRound) {
    return decideLobbyActiveRound(input, command);
  }

  async lobbyRoomLoaded(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandLobbyRoomLoaded) {
    return decideLobbyRoomLoaded(input, command);
  }

  async planStartGame(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanStartGame(input, command);
  }

  async gameStarted(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandGameStarted) {
    return decideGameStarted(input, command);
  }

  async planRoll(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanRoll(input, command);
  }

  async rolled(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandRoundMoveAnswered) {
    return decideRolled(input, command);
  }

  async planPeek(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanPeek(input, command);
  }

  async peeked(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandPeeked) {
    return decidePeeked(input, command);
  }

  async planAnnounce(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandAnnounce) {
    return decidePlanAnnounce(input, command);
  }

  async claimAccepted(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandClaimAccepted) {
    return decideClaimAccepted(input, command);
  }

  async planBelieve(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanBelieve(input, command);
  }

  async believeAccepted(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandBelieveAccepted) {
    return decideBelieveAccepted(input, command);
  }

  async planRollFirst(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanRollFirst(input, command);
  }

  async rollFirstAccepted(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandBelieveAccepted) {
    return decideRollFirstAccepted(input, command);
  }

  async planLift(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanLift(input, command);
  }

  async liftAnswered(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandRoundMoveAnswered) {
    return decideLiftAnswered(input, command);
  }

  async planPlayAgain(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return decidePlanPlayAgain(input, command);
  }

  async playAgainAnswered(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandPlayAgainAnswered) {
    return decidePlayAgainAnswered(input, command);
  }

  async leaveTable(_ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandLeave) {
    return decideLeaveTable(input, command);
  }
  // ── the session (FE2) ───────────────────────────────────────────────────────────────────────────

  async sessionStarted(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandSessionStarted) {
    return decideSessionStarted(input, command);
  }

  async planEntry(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandPlanEntry) {
    return decidePlanEntry(input, command);
  }

  async meAnswered(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMeAnswered) {
    return decideMeAnswered(input, command);
  }

  async meUnreachable(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decideMeUnreachable(input, command);
  }

  async resumeValidated(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandResumeValidated) {
    return decideResumeValidated(input, command);
  }

  async planRestore(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandPlanRestore) {
    return decidePlanRestore(input, command);
  }

  async restoreJoined(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandRestoreJoined) {
    return decideRestoreJoined(input, command);
  }

  async roundRestored(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandRoundRestored) {
    return decideRoundRestored(input, command);
  }

  async planIntent(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandPlanIntent) {
    return decidePlanIntent(input, command);
  }

  async entryFinished(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decideEntryFinished(input, command);
  }

  async planJoin(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decidePlanJoin(input, command);
  }

  async joinRouted(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandJoinRouted) {
    return decideJoinRouted(input, command);
  }

  async tableJoined(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableJoined) {
    return decideTableJoined(input, command);
  }

  async tableRejoined(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableRejoined) {
    return decideTableRejoined(input, command);
  }

  async tableLateJoined(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableLateJoined) {
    return decideTableLateJoined(input, command);
  }

  async planHost(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decidePlanHost(input, command);
  }

  async tableHosted(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableHosted) {
    return decideTableHosted(input, command);
  }

  async planColdRejoin(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decidePlanColdRejoin(input, command);
  }

  async activeRoomFound(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandActiveRoomFound) {
    return decideActiveRoomFound(input, command);
  }

  async planGuest(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandCreateGuest) {
    return decidePlanGuest(input, command);
  }

  async guestCreated(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandGuestCreated) {
    return decideGuestCreated(input, command);
  }

  async planTableCode(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableCode) {
    return decidePlanTableCode(input, command);
  }

  async tableCodeChecked(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandTableCodeChecked) {
    return decideTableCodeChecked(input, command);
  }

  async navigate(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNavigate) {
    return decideNavigate(input, command);
  }

  async dismissJoinError(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decideDismissJoinError(input, command);
  }

  // ── the account (FE3) ───────────────────────────────────────────────────────────────────────────

  async planLoginCode(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmail) {
    return decidePlanLoginCode(input, command);
  }

  async loginCodeAnswered(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandLoginCodeAnswered) {
    return decideLoginCodeAnswered(input, command);
  }

  async planSignUp(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandSignUp) {
    return decidePlanSignUp(input, command);
  }

  async signedUp(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandSignedUp) {
    return decideSignedUp(input, command);
  }

  async planVerifyCode(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandVerifyCode) {
    return decidePlanVerifyCode(input, command);
  }

  async signedIn(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandSignedIn) {
    return decideSignedIn(input, command);
  }

  async profileLoaded(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandProfileLoaded) {
    return decideProfileLoaded(input, command);
  }

  async planNickname(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNickname) {
    return decidePlanNickname(input, command);
  }

  async nicknameChanged(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNickname) {
    return decideNicknameChanged(input, command);
  }

  async planEmailChange(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmail) {
    return decidePlanEmailChange(input, command);
  }

  async planVerifyEmailChange(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmailCode) {
    return decidePlanVerifyEmailChange(input, command);
  }

  async emailChanged(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmailChanged) {
    return decideEmailChanged(input, command);
  }

  async signedOut(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decideSignedOut(input, command);
  }

  // ── the legal side (FE4a) ───────────────────────────────────────────────────────────────────────

  async planLegalReview(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandPlanLegalReview) {
    return decidePlanLegalReview(input, command);
  }

  async legalReviewOf(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandLegalReviewOf) {
    return decideLegalReviewOf(input, command);
  }

  async planLegalAcceptance(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandLegalAcceptance) {
    return decidePlanLegalAcceptance(input, command);
  }

  // ── cookie consent (FE4b) ───────────────────────────────────────────────────────────────────────

  async consentChanged(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandConsentChanged) {
    return decideConsentChanged(input, command);
  }

  async planCookieConsentRecord(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandCookieConsentRecord) {
    return decidePlanCookieConsentRecord(input, command);
  }

  // ── the account's credits (FE4c) ────────────────────────────────────────────────────────────────

  async creditsChanged(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandCreditsChanged) {
    return decideCreditsChanged(input, command);
  }

  async balanceRead(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandBalanceRead) {
    return decideBalanceRead(input, command);
  }

  async planAccountWatch(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decidePlanAccountWatch(input, command);
  }

  // ── the ad slot (FE4d) ──────────────────────────────────────────────────────────────────────────

  async planAdConfig(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decidePlanAdConfig(input, command);
  }

  async adConfigLoaded(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandAdConfigLoaded) {
    return decideAdConfigLoaded(input, command);
  }

  // ── the table's video chat (FE5c) ───────────────────────────────────────────────────────────────

  async mediaToggled(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMediaClock) {
    return decideMediaToggled(input, command);
  }

  async mediaReconnect(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMediaClock) {
    return decideMediaReconnect(input, command);
  }

  async mediaIdleChecked(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMediaIdle) {
    return decideMediaIdleChecked(input, command);
  }

  async mediaRemovedByServer(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandNothing) {
    return decideMediaRemovedByServer(input, command);
  }

  async mediaBannerDismissed(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMediaBanner) {
    return decideMediaBannerDismissed(input, command);
  }

  async planPresence(_ctx: IContextRequest, input: IPropsClientInput, command: ICommandMediaUsed) {
    return decidePlanPresence(input, command);
  }
}
